import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faDownload, 
  faEye, 
  faCheckCircle, 
  faExclamationTriangle,
  faFileAlt,
  faCalendarAlt,
  faBuilding,
  faClipboardList,
  faSpinner,
  faClock,
  faUser,
  faPercent,
  faImage,
  faEyeSlash,
  faBox,
  faCode,
  faCheck
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { getSubmissionAnswers, exportSubmissionAsPDF, exportSubmissionAsHTML, downloadSubmissionAttachment, getAttachmentPreview } from '../../services/submissionApi';
import { getArticlesByIds } from '../../services/articleApi';
import api from '../../services/api';
import Loader from '../common/Loader';
import JSZip from 'jszip';

interface SubmissionAnswersModalProps {
  submissionId: string;
  onClose: () => void;
}

interface Answer {
  questionId: string;
  answer: any;
  attachments?: string[];
  answeredAt: string;
}

interface Question {
  id: string;
  type: string;
  label: { de: string; en: string };
  options?: Array<{ value: string; label: { de: string; en: string } }>;
  required: boolean;
}

const SubmissionAnswersModal: React.FC<SubmissionAnswersModalProps> = ({
  submissionId,
  onClose
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [exportingHTML, setExportingHTML] = useState(false);
  const [showArticles, setShowArticles] = useState<{[questionId: string]: boolean}>({});
  const [articleDetails, setArticleDetails] = useState<{[articleId: string]: any}>({});
  const [articleModal, setArticleModal] = useState<{
    questionId: string;
    articles: any;
    isOpen: boolean;
  } | null>(null);
  
  // **NEUE ARTIKEL-EXPORT STATES**
  const [showArticleSelector, setShowArticleSelector] = useState(false);
  const [selectedArticles, setSelectedArticles] = useState<string[]>([]);
  const [availableArticles, setAvailableArticles] = useState<any[]>([]);
  const [exportingArticleHTML, setExportingArticleHTML] = useState(false);

  // **LOAD SUBMISSION ANSWERS**
  useEffect(() => {
    const loadAnswers = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await getSubmissionAnswers(submissionId);
        
        // **PRÜFE OB RESPONSE {success: true, data: {...}} FORMAT HAT**
        const responseData = (response.data as any).success ? (response.data as any).data : response.data;
        
        setData(responseData);
        
        // **SAMMLE ALLE ARTIKEL AUS DEN ANTWORTEN**
        const allArticleIds = new Set<string>();
        if (responseData?.submission?.answers) {
          responseData.submission.answers.forEach((answer: Answer) => {
            if (answer.answer && typeof answer.answer === 'object' && answer.answer.articles) {
              if (Array.isArray(answer.answer.articles)) {
                answer.answer.articles.forEach((articleId: string) => allArticleIds.add(articleId));
              } else if (typeof answer.answer.articles === 'object') {
                Object.values(answer.answer.articles).forEach((articleIds: any) => {
                  if (Array.isArray(articleIds)) {
                    articleIds.forEach((articleId: string) => allArticleIds.add(articleId));
                  }
                });
              }
            }
          });
        }
        
        // **LADE ARTIKEL-DETAILS FÜR ALLE GEFUNDENEN ARTIKEL**
        if (allArticleIds.size > 0) {
          const articleIdArray = Array.from(allArticleIds);
          await loadArticleDetails(articleIdArray);
        }
        
      } catch (err: any) {
        console.error('❌ Error loading submission answers:', err);
        setError(err.message || 'Fehler beim Laden der Antworten');
      } finally {
        setLoading(false);
      }
    };

    if (submissionId) {
      loadAnswers();
    }
  }, [submissionId]);

  // **UPDATE AVAILABLE ARTICLES WHEN ARTICLE DETAILS CHANGE**
  useEffect(() => {
    // **SETZE VERFÜGBARE ARTIKEL AUS ARTICLEDETAILS**
    if (Object.keys(articleDetails).length > 0) {
      const articlesArray = Object.keys(articleDetails)
        .map(id => ({
          id,
          ...articleDetails[id]
        }))
        .sort((a, b) => (a.articleNumber || '').localeCompare(b.articleNumber || ''));

      setAvailableArticles(articlesArray);
    }
  }, [articleDetails]);

  // **EXPORT PDF HANDLER**
  const handleExportPDF = async () => {
    try {
      setExportingPDF(true);
      await exportSubmissionAsPDF(submissionId);
    } catch (error: any) {
      console.error('PDF export error:', error);
      alert('Fehler beim PDF-Export: ' + error.message);
    } finally {
      setExportingPDF(false);
    }
  };

  // **EXPORT HTML HANDLER**
  const handleExportHTML = async () => {
    try {
      setExportingHTML(true);
      await exportSubmissionAsHTML(submissionId);
    } catch (error: any) {
      console.error('HTML export error:', error);
      alert('Fehler beim HTML-Export: ' + error.message);
    } finally {
      setExportingHTML(false);
    }
  };

  // **ARTIKEL-SPEZIFISCHER HTML EXPORT**
  const handleExportArticleHTML = async () => {
    if (selectedArticles.length === 0) {
      alert('Bitte wählen Sie mindestens einen Artikel aus.');
      return;
    }

    try {
      setExportingArticleHTML(true);
      
      if (selectedArticles.length === 1) {
        // **EINZELNER ARTIKEL EXPORT**
        const articleId = selectedArticles[0];
        const article = availableArticles.find(a => a.id === articleId);
        const htmlContent = generateArticleSpecificHTML(articleId);
        
        const fileName = `${article?.articleNumber || articleId}_${new Date().toISOString().split('T')[0]}.html`;
        
        // Download als einzelne Datei
        const blob = new Blob([htmlContent], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
      } else {
        // **MEHRERE ARTIKEL - ZIP EXPORT**
        const zip = new JSZip();
        
        for (const articleId of selectedArticles) {
          const article = availableArticles.find(a => a.id === articleId);
          const htmlContent = generateArticleSpecificHTML(articleId);
          const fileName = `${article?.articleNumber || articleId}_${new Date().toISOString().split('T')[0]}.html`;
          
          zip.file(fileName, htmlContent);
        }
        
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const fileName = `Fragebogen_Artikel_Export_${new Date().toISOString().split('T')[0]}.zip`;
        
        // Download ZIP
        const url = URL.createObjectURL(zipBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
      
      setShowArticleSelector(false);
      setSelectedArticles([]);
      
    } catch (error: any) {
      console.error('Article HTML export error:', error);
      alert('Fehler beim Artikel-HTML-Export: ' + error.message);
    } finally {
      setExportingArticleHTML(false);
    }
  };

  // **GENERIERE HTML FÜR SPEZIFISCHEN ARTIKEL**
  const generateArticleSpecificHTML = (targetArticleId: string): string => {
    if (!data) return '';
    
    const article = availableArticles.find(a => a.id === targetArticleId);
    const { submission, supplier, template } = data;
    
    // **VERWENDE DIE GLEICHE STRUKTUR WIE DER NORMALE HTML EXPORT**
    // Aber zeige nur Antworten, die für diesen Artikel relevant sind
    
    const currentDate = new Date().toLocaleDateString('de-DE');
    const currentTime = new Date().toLocaleTimeString('de-DE');
    
    let htmlContent = `
<!DOCTYPE html>
<html lang="de">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Fragebogen Antworten - ${article?.articleNumber || targetArticleId}</title>
    <style>
        body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            line-height: 1.6;
            color: #333;
            max-width: 800px;
            margin: 0 auto;
            padding: 20px;
            background-color: #f5f5f5;
        }
        .container {
            background: white;
            padding: 30px;
            border-radius: 10px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }
        .header {
            text-align: center;
            border-bottom: 3px solid #007bff;
            padding-bottom: 20px;
            margin-bottom: 30px;
        }
        .header h1 {
            color: #007bff;
            margin: 0;
            font-size: 28px;
        }
        .article-info {
            background: #e3f2fd;
            padding: 15px;
            border-radius: 8px;
            margin-bottom: 20px;
            border-left: 4px solid #2196f3;
        }
        .article-info h2 {
            margin: 0 0 10px 0;
            color: #1976d2;
        }
        .supplier-info {
            background: #f8f9fa;
            padding: 15px;
            border-radius: 5px;
            margin-bottom: 20px;
        }
        .question {
            margin-bottom: 25px;
            padding: 15px;
            border: 1px solid #ddd;
            border-radius: 5px;
            background: #fafafa;
        }
        .question-title {
            font-weight: bold;
            color: #495057;
            margin-bottom: 10px;
            font-size: 16px;
        }
        .answer {
            padding: 10px 15px;
            background: white;
            border-radius: 4px;
            border-left: 4px solid #28a745;
            margin-top: 8px;
        }
        .timestamp {
            font-size: 12px;
            color: #6c757d;
            margin-top: 5px;
        }
        .footer {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px solid #ddd;
            text-align: center;
            color: #6c757d;
            font-size: 12px;
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>Fragebogen Antworten</h1>
            <p>Artikel-spezifischer Export</p>
        </div>
        
        <div class="article-info">
            <h2>Artikel: ${article?.articleNumber || targetArticleId}</h2>
            ${article?.name ? `<p><strong>Bezeichnung:</strong> ${article.name}</p>` : ''}
            ${article?.brand ? `<p><strong>Marke:</strong> ${article.brand}</p>` : ''}
        </div>
        
        <div class="supplier-info">
            <h3>Lieferant</h3>
            <p><strong>Firma:</strong> ${supplier?.name || 'Unbekannt'}</p>
            ${supplier?.contactPerson ? `<p><strong>Kontaktperson:</strong> ${supplier.contactPerson}</p>` : ''}
            ${supplier?.email ? `<p><strong>E-Mail:</strong> ${supplier.email}</p>` : ''}
        </div>
        
        <div class="questions">`;

    // **SORTIERE ANTWORTEN NACH FRAGEN-REIHENFOLGE**
    if (data.submission?.answers && template?.questions) {
      // **ERSTELLE EINE LISTE DER RELEVANTEN ANTWORTEN IN RICHTIGER REIHENFOLGE**
      const relevantAnswers: any[] = [];
      
      template.questions.forEach((question: any, questionIndex: number) => {
        const answer = data.submission.answers.find((a: any) => a.questionId === question.id);
        
        if (answer) {
          // **PRÜFE OB DIESE ANTWORT FÜR DIESEN ARTIKEL RELEVANT IST**
          let isRelevantForArticle = false;
          
          if (answer.answer && typeof answer.answer === 'object' && answer.answer.articles) {
            if (Array.isArray(answer.answer.articles)) {
              isRelevantForArticle = answer.answer.articles.includes(targetArticleId);
            } else if (typeof answer.answer.articles === 'object') {
              isRelevantForArticle = Object.values(answer.answer.articles).some((articleIds: any) => 
                Array.isArray(articleIds) && articleIds.includes(targetArticleId)
              );
            }
          } else if (!answer.answer || typeof answer.answer !== 'object' || !answer.answer.articles) {
            // **ALLGEMEINE FRAGEN (OHNE ARTIKEL-BEZUG) AUCH ANZEIGEN**
            isRelevantForArticle = true;
          }
          
          if (isRelevantForArticle) {
            relevantAnswers.push({
              question,
              answer,
              questionIndex: questionIndex + 1
            });
          }
        }
      });
      
      // **ZEIGE ALLE RELEVANTEN ANTWORTEN IN RICHTIGER REIHENFOLGE**
      relevantAnswers.forEach(({ question, answer, questionIndex }) => {
        const questionTitle = question.label?.de || question.question || `Frage ${questionIndex}`;
        
        let answerText = '';
        if (typeof answer.answer === 'string') {
          answerText = answer.answer;
        } else if (typeof answer.answer === 'object' && answer.answer.value) {
          if (Array.isArray(answer.answer.value)) {
            answerText = answer.answer.value.join(', ');
          } else {
            answerText = answer.answer.value;
          }
        } else {
          answerText = 'Komplexe Antwort';
        }
        
        const answeredAt = new Date(answer.answeredAt).toLocaleString('de-DE');
        
        htmlContent += `
          <div class="question">
              <div class="question-title">${questionTitle}</div>
              <div class="answer">
                  ${answerText}
              </div>
              <div class="timestamp">Beantwortet am: ${answeredAt}</div>
          </div>`;
      });
    }

    htmlContent += `
        </div>
        
        <div class="footer">
            <p>Export erstellt am ${currentDate} um ${currentTime}</p>
            <p>Template Version: ${template?.version || '1.0'}</p>
        </div>
    </div>
</body>
</html>`;

    return htmlContent;
  };

  // **ARTIKEL SELECTION HANDLERS**
  const handleArticleToggle = (articleId: string) => {
    setSelectedArticles(prev => 
      prev.includes(articleId) 
        ? prev.filter(id => id !== articleId)
        : [...prev, articleId]
    );
  };

  const handleSelectAllArticles = () => {
    if (selectedArticles.length === availableArticles.length) {
      setSelectedArticles([]);
    } else {
      setSelectedArticles(availableArticles.map(a => a.id));
    }
  };

  // **HELPER: FORMAT ANSWER VALUE**
  const generateSubmissionHTML = ({ submission, supplier, template, questions, articleDetails }: any) => {
    const statusLabels: { [key: string]: string } = {
      'pending': 'Ausstehend',
      'in_progress': 'In Bearbeitung',
      'completed': 'Abgeschlossen',
      'expired': 'Abgelaufen'
    };

    const typeLabels: { [key: string]: string } = {
      'manufacturer': 'Hersteller',
      'distributor': 'Händler',
      'service_provider': 'Dienstleister',
      'other': 'Sonstiges'
    };

    const supplierName = supplier?.name || 'Unbekannter Lieferant';
    const templateTitle = template?.title?.de || 'Fragebogen';
    const statusLabel = statusLabels[submission?.status] || submission?.status || 'Unbekannt';
    const typeLabel = typeLabels[supplier?.type] || supplier?.type || 'Unbekannt';
    
    const createdDate = submission?.createdAt ? new Date(submission.createdAt).toLocaleDateString('de-DE') : '';
    const dueDate = submission?.deadline ? new Date(submission.deadline).toLocaleDateString('de-DE') : '';
    const completedDate = submission?.completedAt ? new Date(submission.completedAt).toLocaleDateString('de-DE') : '';
    
    const questionsTotal = questions?.filter((q: any) => q.type !== 'section').length || 0;
    const questionsAnswered = submission?.answers?.length || 0;
    const progressPercentage = questionsTotal > 0 ? Math.round((questionsAnswered / questionsTotal) * 100) : 0;

    // Generate questions HTML
    let questionsHTML = '';
    if (questions && Array.isArray(questions)) {
      questions.forEach((question: any, index: number) => {
        const answer = submission?.answers?.find((a: any) => a.questionId === question.id);
        questionsHTML += generateQuestionHTML(question, answer, index + 1);
      });
    }

    return `
      <!DOCTYPE html>
      <html lang="de">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${supplierName} - ${templateTitle}</title>
        <style>
          ${getHTMLStyles()}
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <div class="header-left">
              <h1>${supplierName}</h1>
              <div class="template-title">${templateTitle}</div>
            </div>
            <div class="header-right">
              <div class="status-badge status-${submission?.status || 'unknown'}">${statusLabel}</div>
              <div class="progress-info">${questionsAnswered}/${questionsTotal} Fragen (${progressPercentage}%)</div>
            </div>
          </div>
          
          <div class="overview-section">
            <h2>Übersicht</h2>
            <div class="overview-grid">
              <div class="overview-item">
                <strong>Lieferantentyp:</strong> ${typeLabel}
              </div>
              <div class="overview-item">
                <strong>E-Mail:</strong> ${supplier?.email || 'Nicht angegeben'}
              </div>
              <div class="overview-item">
                <strong>Kontaktperson:</strong> ${supplier?.contactPerson || 'Nicht angegeben'}
              </div>
              <div class="overview-item">
                <strong>GLN:</strong> ${supplier?.gln || 'Nicht angegeben'}
              </div>
              <div class="overview-item">
                <strong>EK-Bereich:</strong> ${supplier?.assignedEK || 'Nicht zugewiesen'}
              </div>
              <div class="overview-item">
                <strong>Bearbeiter:</strong> ${supplier?.assignedUser || 'Nicht zugewiesen'}
              </div>
              <div class="overview-item">
                <strong>Erstellt:</strong> ${createdDate}
              </div>
              <div class="overview-item">
                <strong>Fällig:</strong> ${dueDate || 'Kein Datum'}
              </div>
              <div class="overview-item">
                <strong>Abgeschlossen:</strong> ${completedDate || 'Noch nicht abgeschlossen'}
              </div>
            </div>
          </div>
          
          <div class="questions-section">
            <h2>Fragebogen-Antworten</h2>
            ${questionsHTML}
          </div>
          
          <div class="footer-info">
            Export erstellt am ${new Date().toLocaleDateString('de-DE')} um ${new Date().toLocaleTimeString('de-DE')}
          </div>
        </div>
      </body>
      </html>
    `;
  };

  // **GENERATE QUESTION HTML**
  const generateQuestionHTML = (question: any, answer: any, questionNumber: number) => {
    let formattedAnswer = '';
    let answerClass = '';
    
    if (!answer || answer.answer === null || answer.answer === undefined || answer.answer === '') {
      formattedAnswer = '<span class="no-answer">Nicht beantwortet</span>';
      answerClass = 'no-answer';
    } else {
      const formattedData = formatAnswer(question, answer);
      formattedAnswer = formattedData.text;
      answerClass = formattedData.type;

      // Füge Artikel-Details hinzu, falls vorhanden
      if (formattedData.articles) {
        const articlesList = generateArticleListHTML(formattedData.articles);
        if (articlesList) {
          formattedAnswer += articlesList;
        }
      }
    }
    
    const questionText = question.label?.de || 'Unbekannte Frage';
    const isRequired = question.required;
    const answeredDate = answer?.answeredAt ? new Date(answer.answeredAt).toLocaleDateString('de-DE') : '';
    
    return `
      <div class="question-item">
        <div class="question-header">
          <div class="question-number">${questionNumber}</div>
          <div class="question-text">
            ${questionText}
            ${isRequired ? '<span class="required">*</span>' : ''}
          </div>
        </div>
        <div class="answer-content ${answerClass}">
          ${formattedAnswer}
        </div>
        ${answeredDate ? `<div class="answer-date">Beantwortet am: ${answeredDate}</div>` : ''}
      </div>
    `;
  };

  // **GENERATE ARTICLE LIST HTML**
  const generateArticleListHTML = (articles: any) => {
    if (!articles) return '';
    
    let articleHTML = '<div class="article-details">';
    
    if (Array.isArray(articles)) {
      // Für Text-Eingaben: einfaches Array
      if (articles.length > 0) {
        articleHTML += '<div class="article-section"><strong>Artikel:</strong>';
        articles.forEach(articleId => {
          const article = articleDetails?.[articleId];
          if (article) {
            articleHTML += `<div class="article-item">• ${article.name || 'Unbenannt'} (${article.articleNumber || 'N/A'})</div>`;
          } else {
            articleHTML += `<div class="article-item">• Artikel ID: ${articleId}</div>`;
          }
        });
        articleHTML += '</div>';
      }
    } else if (typeof articles === 'object') {
      // Für Ja/Nein oder Option-basierte Fragen
      Object.entries(articles).forEach(([key, articleIds]: [string, any]) => {
        if (Array.isArray(articleIds) && articleIds.length > 0) {
          const optionLabel = key === 'ja' ? 'Ja' : key === 'nein' ? 'Nein' : key;
          articleHTML += `<div class="article-section"><strong>${optionLabel}-Antwort (${articleIds.length} Artikel):</strong>`;
          articleIds.forEach(articleId => {
            const article = articleDetails?.[articleId];
            if (article) {
              articleHTML += `<div class="article-item">• ${article.name || 'Unbenannt'} (${article.articleNumber || 'N/A'})</div>`;
            } else {
              articleHTML += `<div class="article-item">• Artikel ID: ${articleId}</div>`;
            }
          });
          articleHTML += '</div>';
        }
      });
    }
    
    articleHTML += '</div>';
    return articleHTML;
  };

  // **HTML STYLES**
  const getHTMLStyles = () => `
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      line-height: 1.6;
      color: #333;
      background-color: #f5f5f5;
    }
    
    .container {
      max-width: 1200px;
      margin: 20px auto;
      background: white;
      box-shadow: 0 0 20px rgba(0,0,0,0.1);
      border-radius: 8px;
      overflow: hidden;
    }
    
    .header {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      padding: 30px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-wrap: wrap;
    }
    
    .header h1 {
      font-size: 2.5rem;
      margin-bottom: 5px;
    }
    
    .template-title {
      font-size: 1.2rem;
      opacity: 0.9;
    }
    
    .status-badge {
      padding: 8px 16px;
      border-radius: 20px;
      font-weight: bold;
      font-size: 0.9rem;
      text-transform: uppercase;
      margin-bottom: 10px;
    }
    
    .status-pending { background: #fef3cd; color: #856404; }
    .status-in_progress { background: #d1ecf1; color: #0c5460; }
    .status-completed { background: #d4edda; color: #155724; }
    .status-expired { background: #f8d7da; color: #721c24; }
    .status-unknown { background: #e2e3e5; color: #495057; }
    
    .progress-info {
      font-size: 1.1rem;
      font-weight: 500;
    }
    
    .overview-section, .questions-section {
      padding: 30px;
    }
    
    .overview-section h2, .questions-section h2 {
      color: #667eea;
      margin-bottom: 15px;
      padding-bottom: 10px;
      border-bottom: 2px solid #e9ecef;
      font-size: 1.5rem;
    }
    
    .questions-section h2 {
      margin-bottom: 15px;
    }
    
    .overview-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      gap: 15px;
    }
    
    .overview-item {
      background: #f8f9fa;
      padding: 15px;
      border-radius: 6px;
      border-left: 4px solid #667eea;
    }
    
    .question-item {
      background: #f8f9fa;
      margin-bottom: 20px;
      border-radius: 8px;
      overflow: hidden;
      border: 1px solid #e9ecef;
    }
    
    .question-header {
      background: #e9ecef;
      padding: 15px;
      display: flex;
      align-items: center;
    }
    
    .question-number {
      background: #667eea;
      color: white;
      width: 30px;
      height: 30px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      margin-right: 15px;
      flex-shrink: 0;
    }
    
    .question-text {
      font-weight: 600;
      font-size: 1.1rem;
    }
    
    .required {
      color: #dc3545;
      margin-left: 5px;
    }
    
    .answer-content {
      padding: 20px;
      background: white;
    }
    
    .no-answer {
      color: #6c757d;
      font-style: italic;
    }
    
    .answer-date {
      padding: 10px 20px;
      background: #f1f3f4;
      color: #5f6368;
      font-size: 0.9rem;
      border-top: 1px solid #e9ecef;
    }
    
    .article-details {
      margin-top: 15px;
      padding: 15px;
      background: #f8f9fa;
      border-radius: 6px;
      border-left: 4px solid #28a745;
    }
    
    .article-section {
      margin-bottom: 10px;
    }
    
    .article-section:last-child {
      margin-bottom: 0;
    }
    
    .article-item {
      margin-left: 15px;
      margin-bottom: 5px;
      color: #495057;
    }
    
    .footer-info {
      text-align: center;
      padding: 20px;
      background: #f8f9fa;
      color: #6c757d;
      font-size: 0.9rem;
      border-top: 1px solid #e9ecef;
    }
    
    @media print {
      body { background: white; }
      .container { box-shadow: none; margin: 0; }
      .question-item { break-inside: avoid; }
    }
    
    @media (max-width: 768px) {
      .header {
        flex-direction: column;
        align-items: flex-start;
      }
      
      .header-right {
        margin-top: 20px;
      }
      
      .overview-grid {
        grid-template-columns: 1fr;
      }
    }
  `;

  // **LOAD ARTICLE DETAILS**
  const loadArticleDetails = async (articleIds: string[]) => {
    try {
      const articles = await getArticlesByIds(articleIds);
      
      const articleMap: {[key: string]: any} = {};
      articles.forEach(article => {
        articleMap[article._id] = article;
      });
      
      setArticleDetails(prev => {
        const updated = {
          ...prev,
          ...articleMap
        };
        return updated;
      });
      
    } catch (error) {
      console.error('❌ Error loading article details:', error);
    }
  };

  // **HELPER: FORMAT ANSWER VALUE**
  const formatAnswer = (question: Question, answer: Answer | undefined) => {
    if (!answer) {
      return { 
        text: 'Nicht beantwortet', 
        type: 'empty',
        color: 'text-gray-400'
      };
    }

    const value = answer.answer;
    
    // Special handling for files
    if (question.type === 'file') {
      const attachments = answer.attachments || [];
      if (attachments.length > 0) {
        return { 
          text: `${attachments.length} Datei(en) hochgeladen`, 
          type: 'file',
          color: 'text-blue-600'
        };
      }
      if (value && ((Array.isArray(value) && value.length > 0) || 
                    (typeof value === 'object' && value !== null) ||
                    (typeof value === 'string' && value.length > 0 && value !== '[]'))) {
        return { 
          text: '1 Datei hochgeladen', 
          type: 'file',
          color: 'text-blue-600'
        };
      }
      return { 
        text: 'Keine Dateien hochgeladen', 
        type: 'empty',
        color: 'text-gray-400'
      };
    }

    // Special handling for signature
    if (question.type === 'signature') {
      if (value === true || value === 'signed' || value === '1' || value === 1 ||
          (value && typeof value === 'object') ||
          (typeof value === 'string' && (value.startsWith('data:') || value.length > 10))) {
        return { 
          text: 'Unterschrift vorhanden', 
          type: 'signature',
          color: 'text-green-600',
          preview: typeof value === 'string' && value.startsWith('data:image/') ? value : null
        };
      }
      return { 
        text: 'Keine Unterschrift', 
        type: 'empty',
        color: 'text-gray-400'
      };
    }

    // Check if answer is empty
    if (value === null || value === undefined || value === '') {
      return { 
        text: 'Nicht beantwortet', 
        type: 'empty',
        color: 'text-gray-400'
      };
    }

    switch (question.type) {
      case 'radio':
      case 'select':
        // **NEUES FORMAT: {value: 'optionValue', articles: {optionValue: [...]}}**
        if (typeof value === 'object' && value !== null && value.value && value.articles) {
          const selectedOption = question.options?.find(opt => opt.value === value.value);
          const optionLabel = selectedOption?.label?.de || value.value;
          
          // Zähle alle Artikel über alle Optionen
          let totalArticleCount = 0;
          if (typeof value.articles === 'object') {
            Object.values(value.articles).forEach((articleList: any) => {
              if (Array.isArray(articleList)) {
                totalArticleCount += articleList.length;
              }
            });
          }
          
          const articleText = totalArticleCount > 0 ? ` (${totalArticleCount} Artikel ausgewählt)` : '';
          
          return { 
            text: `${optionLabel}${articleText}`, 
            type: 'option-with-articles',
            color: 'text-gray-900',
            articles: value.articles
          };
        }
        
        const selectedOption = question.options?.find(opt => opt.value === value);
        return { 
          text: selectedOption?.label?.de || value, 
          type: 'option',
          color: 'text-gray-900'
        };

      case 'multiselect':
        // **NEUES FORMAT: {value: ['option1', 'option2'], articles: {option1: [...], option2: [...]}}**
        if (typeof value === 'object' && value !== null && value.value && value.articles) {
          const selectedValues = Array.isArray(value.value) ? value.value : [value.value];
          const selectedLabels = selectedValues.map((val: any) => {
            const option = question.options?.find(opt => opt.value === val);
            return option?.label?.de || val;
          });
          
          // Zähle alle Artikel über alle Optionen
          let totalArticleCount = 0;
          if (typeof value.articles === 'object') {
            Object.values(value.articles).forEach((articleList: any) => {
              if (Array.isArray(articleList)) {
                totalArticleCount += articleList.length;
              }
            });
          }
          
          const articleText = totalArticleCount > 0 ? ` (${totalArticleCount} Artikel ausgewählt)` : '';
          
          return { 
            text: `${selectedLabels.join(', ')}${articleText}`, 
            type: 'multiple-with-articles',
            color: 'text-gray-900',
            articles: value.articles
          };
        }
        
        if (Array.isArray(value)) {
          const selectedLabels = value.map(val => {
            const option = question.options?.find(opt => opt.value === val);
            return option?.label?.de || val;
          });
          return { 
            text: selectedLabels.join(', '), 
            type: 'multiple',
            color: 'text-gray-900'
          };
        }
        return { 
          text: String(value), 
          type: 'text',
          color: 'text-gray-900'
        };

      case 'checkbox':
        if (value === true || value === 'yes' || value === 'ja') {
          return { text: 'Ja', type: 'boolean', color: 'text-green-600' };
        }
        if (value === false || value === 'no' || value === 'nein') {
          return { text: 'Nein', type: 'boolean', color: 'text-red-600' };
        }
        
        // **NEUES FORMAT: {value: 'ja/nein', articles: {ja: [...], nein: [...]}}**
        if (typeof value === 'object' && value !== null && value.value && value.articles) {
          const choice = value.value === 'ja' ? 'Ja' : value.value === 'nein' ? 'Nein' : value.value;
          const jaArticles = value.articles.ja || [];
          const neinArticles = value.articles.nein || [];
          
          let articleText = '';
          if (jaArticles.length > 0 || neinArticles.length > 0) {
            const parts = [];
            if (jaArticles.length > 0) {
              parts.push(`Ja: ${jaArticles.length} Artikel`);
            }
            if (neinArticles.length > 0) {
              parts.push(`Nein: ${neinArticles.length} Artikel`);
            }
            articleText = ` (${parts.join(', ')})`;
          }
          
          return { 
            text: `${choice}${articleText}`, 
            type: 'boolean-with-articles', 
            color: value.value === 'ja' ? 'text-green-600' : 'text-red-600',
            articles: value.articles,
            selectedValue: value.value
          };
        }
        
        // **LEGACY FORMAT: {value: 'ja/nein', selectedArticles: [...]}**
        if (typeof value === 'object' && value !== null && value.value && value.selectedArticles) {
          const choice = value.value === 'ja' ? 'Ja' : value.value === 'nein' ? 'Nein' : value.value;
          const articleCount = Array.isArray(value.selectedArticles) ? value.selectedArticles.length : 0;
          const articleText = articleCount > 0 ? ` (${articleCount} Artikel ausgewählt)` : '';
          
          return { 
            text: `${choice}${articleText}`, 
            type: 'boolean-with-articles', 
            color: value.value === 'ja' ? 'text-green-600' : 'text-red-600',
            articles: { [value.value]: value.selectedArticles },
            selectedValue: value.value
          };
        }
        
        // **OPTIONEN MIT ARTIKELN: {value: ['option1'], articles: {option1: [...]}}**
        if (typeof value === 'object' && value !== null && value.value && value.articles && Array.isArray(value.value)) {
          const selectedValues = value.value;
          const selectedLabels = selectedValues.map((val: any) => {
            const option = question.options?.find(opt => opt.value === val);
            return option?.label?.de || val;
          });
          
          // Zähle alle Artikel über alle Optionen
          let totalArticleCount = 0;
          if (typeof value.articles === 'object') {
            Object.values(value.articles).forEach((articleList: any) => {
              if (Array.isArray(articleList)) {
                totalArticleCount += articleList.length;
              }
            });
          }
          
          const articleText = totalArticleCount > 0 ? ` (${totalArticleCount} Artikel ausgewählt)` : '';
          
          return { 
            text: selectedLabels.length > 0 ? `${selectedLabels.join(', ')}${articleText}` : 'Keine Auswahl', 
            type: 'multiple-with-articles',
            color: selectedLabels.length > 0 ? 'text-gray-900' : 'text-gray-400',
            articles: value.articles
          };
        }
        
        if (Array.isArray(value)) {
          if (question.options && question.options.length > 0) {
            const selectedLabels = value.map((val: any) => {
              const option = question.options?.find(opt => opt.value === val);
              return option?.label?.de || val;
            });
            return { 
              text: selectedLabels.length > 0 ? selectedLabels.join(', ') : 'Keine Auswahl', 
              type: 'multiple',
              color: selectedLabels.length > 0 ? 'text-gray-900' : 'text-gray-400'
            };
          }
          return { 
            text: value.length > 0 ? value.join(', ') : 'Keine Auswahl', 
            type: 'multiple',
            color: value.length > 0 ? 'text-gray-900' : 'text-gray-400'
          };
        }
        return { 
          text: String(value), 
          type: 'text',
          color: 'text-gray-900'
        };

      case 'yesno':
        if (value === true || value === 'yes' || value === 'ja') {
          return { text: 'Ja', type: 'boolean', color: 'text-green-600' };
        }
        if (value === false || value === 'no' || value === 'nein') {
          return { text: 'Nein', type: 'boolean', color: 'text-red-600' };
        }
        return { 
          text: String(value), 
          type: 'text',
          color: 'text-gray-900'
        };

      case 'date':
        try {
          return { 
            text: new Date(value).toLocaleDateString('de-DE'), 
            type: 'date',
            color: 'text-gray-900'
          };
        } catch {
          return { 
            text: String(value), 
            type: 'text',
            color: 'text-gray-900'
          };
        }

      case 'info_text':
        // Strip HTML tags from info_text
        const questionAny = question as any;
        const infoText = question.label?.de || questionAny.text?.de || 'Info';
        const strippedText = infoText.replace(/<[^>]*>/g, '');
        return { 
          text: strippedText, 
          type: 'info_text',
          color: 'text-blue-600'
        };

      case 'text':
      case 'textarea':
        // **NEUES FORMAT: {value: 'text', articles: [...]}**
        if (typeof value === 'object' && value !== null && value.value && value.articles) {
          const articleCount = Array.isArray(value.articles) ? value.articles.length : 0;
          const articleText = articleCount > 0 ? ` (${articleCount} Artikel ausgewählt)` : '';
          
          return { 
            text: `${value.value}${articleText}`, 
            type: `${question.type}-with-articles`,
            color: 'text-gray-900',
            articles: Array.isArray(value.articles) ? { text: value.articles } : {}
          };
        }
        
        return { 
          text: String(value), 
          type: question.type,
          color: 'text-gray-900'
        };

      default:
        if (typeof value === 'object' && value !== null) {
          if (Array.isArray(value)) {
            return { 
              text: value.join(', '), 
              type: 'multiple',
              color: 'text-gray-900'
            };
          } else {
            return { 
              text: 'Daten vorhanden', 
              type: 'object',
              color: 'text-blue-600'
            };
          }
        }
        return { 
          text: String(value), 
          type: 'text',
          color: 'text-gray-900'
        };
    }
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50"
      >
        <div className="bg-white rounded-xl p-8 shadow-2xl">
          <Loader size="large" text="Lade Antworten..." />
        </div>
      </motion.div>
    );
  }

  if (error || !data) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
          <div className="text-center">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 text-4xl mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Fehler</h3>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 transition-colors"
            >
              Schließen
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  const actualData = data?.data || data;
  const { submission, supplier, template } = actualData;
  
  if (!submission || !template) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
          <div className="text-center">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 text-4xl mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Daten nicht verfügbar</h3>
            <p className="text-gray-600 mb-6">Die Submission-Daten konnten nicht geladen werden.</p>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 transition-colors"
            >
              Schließen
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  const answerMap = new Map<string, Answer>();
  
  if (submission.answers && Array.isArray(submission.answers)) {
    submission.answers.forEach((answer: Answer) => {
      answerMap.set(answer.questionId, answer);
    });
  }

  const questions = template.questions?.filter((q: Question) => q.type !== 'section') || [];
  const sections = template.questions?.filter((q: Question) => q.type === 'section') || [];
  
  const answeredCount = actualData?.answeredQuestions || 0;
  const totalCount = actualData?.totalQuestions || 0;
  const progressPercentage = submission?.progressPercentage || 0;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'sent':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return 'Abgeschlossen';
      case 'in_progress': return 'In Bearbeitung';
      case 'sent': return 'Versendet';
      default: return 'Ausstehend';
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        key="main-modal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* **KOMPAKTER HEADER IN WEDL GRAU** */}
          <div className="bg-[#213239] text-white px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="bg-white/10 p-2 rounded-lg">
                  <FontAwesomeIcon icon={faClipboardList} className="text-lg" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold">Fragebogen Antworten</h1>
                  <div className="flex items-center space-x-4 mt-1 text-sm">
                    <div className="flex items-center space-x-1">
                      <FontAwesomeIcon icon={faBuilding} className="text-gray-300" />
                      <span className="text-gray-300">{supplier?.name || 'Unbekannter Lieferant'}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <FontAwesomeIcon icon={faClipboardList} className="text-gray-300" />
                      <span className="text-gray-300">{template?.title?.de || 'Unbekannter Fragebogen'}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleExportPDF}
                  disabled={exportingPDF}
                  className="flex items-center px-3 py-1.5 bg-white/10 text-white rounded text-sm hover:bg-white/20 disabled:opacity-50 transition-all"
                >
                  {exportingPDF ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-1.5" />
                      Exportiert...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faDownload} className="mr-1.5" />
                      PDF Export
                    </>
                  )}
                </button>
                
                <button
                  onClick={handleExportHTML}
                  disabled={exportingHTML}
                  className="flex items-center px-3 py-1.5 bg-white/10 text-white rounded text-sm hover:bg-white/20 disabled:opacity-50 transition-all"
                >
                  {exportingHTML ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-1.5" />
                      Exportiert...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCode} className="mr-1.5" />
                      HTML Export
                    </>
                  )}
                </button>
                
                <button
                  onClick={() => {
                    setShowArticleSelector(true);
                  }}
                  disabled={exportingArticleHTML}
                  className="flex items-center px-3 py-1.5 bg-green-500/20 text-white rounded text-sm hover:bg-green-500/30 disabled:opacity-50 transition-all"
                >
                  <FontAwesomeIcon icon={faBox} className="mr-1.5" />
                  Artikel HTML
                  {availableArticles.length > 0 && (
                    <span className="ml-1 text-xs bg-white/20 px-1.5 py-0.5 rounded-full">
                      {availableArticles.length}
                    </span>
                  )}
                </button>
                
                <button
                  onClick={onClose}
                  className="text-white/80 hover:text-white hover:bg-white/10 rounded p-1.5 transition-all"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-lg" />
                </button>
              </div>
            </div>

            {/* **KOMPAKTE STATISTIKEN** */}
            <div className="flex items-center space-x-6 mt-3 pt-3 border-t border-white/10">
              <div className="flex items-center space-x-2">
                <div className="bg-white/10 p-1.5 rounded">
                  <FontAwesomeIcon icon={faPercent} className="text-white text-sm" />
                </div>
                <div>
                  <div className="text-lg font-bold">{progressPercentage}%</div>
                  <div className="text-xs text-gray-300">Fortschritt</div>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <div className="bg-white/10 p-1.5 rounded">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-white text-sm" />
                </div>
                <div>
                  <div className="text-lg font-bold">{answeredCount}</div>
                  <div className="text-xs text-gray-300">von {totalCount} beantwortet</div>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <div className="bg-white/10 p-1.5 rounded">
                  <FontAwesomeIcon icon={faClock} className="text-white text-sm" />
                </div>
                <div>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getStatusColor(submission?.status || '')}`}>
                    {getStatusText(submission?.status || '')}
                  </span>
                </div>
              </div>
            </div>

            {/* **KOMPAKTE PROGRESS BAR** */}
            <div className="mt-3">
              <div className="bg-white/20 rounded-full h-1.5">
                <div 
                  className="bg-white rounded-full h-1.5 transition-all duration-300"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>
          </div>

          {/* **KOMPAKTER CONTENT** */}
          <div className="flex-1 overflow-y-auto max-h-[calc(95vh-160px)] p-4">
            <div className="space-y-3">
              {questions.map((question: Question, index: number) => {
                const answer = answerMap.get(question.id);
                const hasAnswer = answer && answer.answer !== null && answer.answer !== undefined && answer.answer !== '';
                const formattedAnswer = formatAnswer(question, answer);

                return (
                  <motion.div
                    key={question.id}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.02 }}
                    className={`border rounded-xl p-3.5 transition-all hover:shadow-sm ${
                      hasAnswer ? 'bg-emerald-50 border-emerald-200/70' : 'bg-slate-50 border-slate-200/70'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
                          hasAnswer 
                            ? 'bg-emerald-500 text-white' 
                            : 'bg-slate-300 text-slate-600'
                        }`}>
                          {hasAnswer ? (
                            <FontAwesomeIcon icon={faCheckCircle} />
                          ) : (
                            index + 1
                          )}
                        </div>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <h3 className="text-sm font-semibold text-gray-900 mb-1">
                              {question.label?.de || 'Unbekannte Frage'}
                              {question.required && <span className="text-red-500 ml-1">*</span>}
                            </h3>
                          </div>
                          <div className="flex items-center space-x-2 flex-shrink-0 ml-3">
                            <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-md text-xs font-medium">
                              {question.type}
                            </span>
                          </div>
                        </div>
                        
                        <div className="bg-white rounded-lg p-3 border border-slate-200/70">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="text-xs text-gray-600 mb-1">Antwort:</div>
                              <div className={`text-sm font-medium ${formattedAnswer.color}`}>
                                {formattedAnswer.text}
                              </div>
                              
                              {/* **ARTIKEL ANZEIGE BUTTON** */}
                              {formattedAnswer.articles && (
                                <div className="mt-2">
                                  <button
                                    onClick={async () => {
                                      // Öffne Modal statt Toggle
                                      setArticleModal({
                                        questionId: question.id,
                                        articles: formattedAnswer.articles,
                                        isOpen: true
                                      });
                                      
                                      // Lade Artikel-Details
                                      const allArticleIds: string[] = [];
                                      Object.values(formattedAnswer.articles).forEach((ids: any) => {
                                        if (Array.isArray(ids)) {
                                          allArticleIds.push(...ids);
                                        }
                                      });
                                      
                                      if (allArticleIds.length > 0) {
                                        await loadArticleDetails(allArticleIds);
                                      }
                                    }}
                                    className="flex items-center space-x-2 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
                                  >
                                    <FontAwesomeIcon icon={faBox} />
                                    <span>Artikel anzeigen</span>
                                  </button>
                                </div>
                              )}
                            </div>
                            
                            {answer?.answeredAt && (
                              <div className="text-right text-xs text-gray-500 ml-3">
                                <div className="flex items-center space-x-1">
                                  <FontAwesomeIcon icon={faCalendarAlt} />
                                  <span>{new Date(answer.answeredAt).toLocaleDateString('de-DE')}</span>
                                </div>
                                <div className="flex items-center space-x-1 mt-0.5">
                                  <FontAwesomeIcon icon={faClock} />
                                  <span>{new Date(answer.answeredAt).toLocaleTimeString('de-DE')}</span>
                                </div>
                              </div>
                            )}
                          </div>
                          
                          {/* **SIGNATURE PREVIEW** */}
                          {formattedAnswer.preview && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <div className="text-xs text-gray-600 mb-2">Unterschrift:</div>
                              <img 
                                src={formattedAnswer.preview} 
                                alt="Unterschrift" 
                                className="max-w-md max-h-24 border border-gray-300 rounded bg-white shadow-sm"
                                style={{ imageRendering: 'crisp-edges' }}
                              />
                            </div>
                          )}
                          
                          {/* **ATTACHMENTS** */}
                          {answer?.attachments && answer.attachments.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <div className="flex items-center space-x-2 mb-2">
                                <FontAwesomeIcon icon={faFileAlt} className="text-blue-600" />
                                <span className="text-xs font-medium text-gray-700">
                                  {answer.attachments.length} Anhang(e):
                                </span>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {answer.attachments.map((att: any, idx: number) => (
                                  <AttachmentItem
                                    key={idx}
                                    attachment={att}
                                    submissionId={submissionId}
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
              
              {questions.length === 0 && (
                <div className="text-center py-8">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="text-3xl text-gray-300 mb-3" />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">Keine Fragen gefunden</h3>
                  <p className="text-gray-500 text-sm">Dieses Template enthält keine Fragen zum Anzeigen.</p>
                </div>
              )}
            </div>
          </div>

          {/* **KOMPAKTER FOOTER** */}
          <div className="border-t border-slate-200/70 bg-slate-50 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center space-x-3 text-xs text-slate-500">
              <span>Template Version: {template?.version || '1.0'}</span>
              <span>•</span>
              <span>{questions.length} Fragen</span>
              <span>•</span>
              <span>Erstellt am {new Date(submission?.createdAt || Date.now()).toLocaleDateString('de-DE')}</span>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-slate-100 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-200 transition-all"
              >
                Schließen
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* **ARTIKEL MODAL** */}
      {articleModal && articleModal.isOpen && (
        <motion.div
          key="article-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4"
          onClick={() => setArticleModal(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-200/70 bg-white">
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center"><FontAwesomeIcon icon={faBox} /></span>
                <h3 className="text-base font-semibold text-slate-900">
                  Ausgewählte Artikel
                </h3>
              </div>
              <button
                onClick={() => setArticleModal(null)}
                className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Content */}
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              <div className="space-y-4">
                {Object.entries(articleModal.articles).map(([option, articleIds]) => {
                  const articles = Array.isArray(articleIds) ? articleIds : [];
                  if (articles.length === 0) return null;
                  
                  return (
                    <div key={option} className="bg-slate-50 rounded-xl p-4 border border-slate-200/70">
                      <div className="flex items-center space-x-2 mb-3">
                        <div className={`w-3 h-3 rounded-full ${
                          option === 'ja' ? 'bg-green-500' : option === 'nein' ? 'bg-red-500' : 'bg-blue-500'
                        }`}></div>
                        <h4 className="font-medium text-gray-900">
                          {option === 'ja' ? 'Ja-Antwort' : option === 'nein' ? 'Nein-Antwort' : option}
                        </h4>
                        <span className="text-sm text-gray-500">
                          ({articles.length} Artikel)
                        </span>
                      </div>
                      <div className="space-y-3">
                        {articles.map((articleId, idx) => {
                          const article = articleDetails[articleId];
                          
                          return (
                            <div key={idx} className="bg-white rounded-lg p-3 border border-slate-200/70">
                              {article ? (
                                <div>
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <h5 className="font-medium text-gray-900">
                                        {article.name || 'Kein Name'}
                                      </h5>
                                      <p className="text-sm text-gray-600 mt-1">
                                        Artikelnummer: {article.articleNumber || 'Keine Artikelnummer'}
                                      </p>
                                      {article.description && (
                                        <p className="text-sm text-gray-500 mt-2">
                                          {article.description}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-center justify-between">
                                  <span className="text-gray-600">
                                    Artikel ID: {articleId}
                                  </span>
                                  <FontAwesomeIcon icon={faSpinner} className="animate-spin text-gray-400" />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Footer */}
            <div className="flex items-center justify-end p-4 border-t border-slate-200/70 bg-slate-50">
              <button
                onClick={() => setArticleModal(null)}
                className="px-4 py-2 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 transition-colors"
              >
                Schließen
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* **ARTIKEL-AUSWAHL MODAL** */}
      {showArticleSelector && (
        <motion.div
          key="article-selector"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[10001]"
          onClick={(e) => e.target === e.currentTarget && setShowArticleSelector(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden"
          >
            <div className="p-6 border-b border-slate-200/70">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-slate-900">Artikel für HTML-Export auswählen</h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Wählen Sie die Artikel aus, für die separate HTML-Dateien erstellt werden sollen.
                  </p>
                </div>
                <button
                  onClick={() => setShowArticleSelector(false)}
                  className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              <div className="mt-4">
                <button
                  onClick={() => {
                    if (selectedArticles.length === availableArticles.length) {
                      setSelectedArticles([]);
                    } else {
                      setSelectedArticles(availableArticles.map(a => a.id));
                    }
                  }}
                  className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                >
                  <div className={`w-4 h-4 border-2 border-white rounded mr-2 flex items-center justify-center ${
                    selectedArticles.length === availableArticles.length ? 'bg-white/20' : ''
                  }`}>
                    {selectedArticles.length === availableArticles.length && (
                      <FontAwesomeIcon icon={faCheck} className="text-white text-xs" />
                    )}
                  </div>
                  {selectedArticles.length === availableArticles.length ? 'Alle abwählen' : 'Alle auswählen'}
                  <span className="ml-2 text-blue-200">({availableArticles.length} Artikel)</span>
                </button>
              </div>
            </div>

            <div className="p-6 max-h-96 overflow-y-auto">
              <div className="space-y-2">
                {availableArticles.map((article: any) => (
                  <div
                    key={article.id}
                    onClick={() => {
                      setSelectedArticles(prev => 
                        prev.includes(article.id) 
                          ? prev.filter(id => id !== article.id)
                          : [...prev, article.id]
                      );
                    }}
                    className="flex items-center p-3 bg-slate-50 hover:bg-slate-100 border border-slate-200/70 rounded-lg cursor-pointer transition-colors"
                  >
                    <div className={`w-5 h-5 border-2 border-slate-300 rounded flex items-center justify-center mr-3 transition-colors ${
                      selectedArticles.includes(article.id) 
                        ? 'bg-red-500 border-red-500' 
                        : 'hover:border-red-300'
                    }`}>
                      {selectedArticles.includes(article.id) && (
                        <FontAwesomeIcon icon={faCheck} className="text-white text-xs" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="font-medium text-gray-900">
                            {article.articleNumber}
                          </div>
                          <div className="text-sm text-gray-600">
                            {article.name || 'Keine Bezeichnung'}
                          </div>
                          {article.brand && (
                            <div className="text-xs text-gray-500 mt-1">
                              Marke: {article.brand}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-6 border-t border-slate-200/70 bg-slate-50">
              <div className="flex items-center justify-between">
                <div className="text-sm text-slate-600">
                  {selectedArticles.length > 0 && (
                    <span className="font-medium">
                      {selectedArticles.length} Artikel ausgewählt
                      {selectedArticles.length > 1 && ' (ZIP-Download)'}
                    </span>
                  )}
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowArticleSelector(false)}
                    className="px-4 py-2 text-slate-700 bg-white border border-slate-200 rounded-xl font-medium hover:bg-slate-100 transition-colors"
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={handleExportArticleHTML}
                    disabled={selectedArticles.length === 0 || exportingArticleHTML}
                    className="px-4 py-2 bg-gradient-to-br from-[#e2313a] to-[#c9252d] text-white rounded-xl font-semibold hover:brightness-105 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center"
                  >
                    {exportingArticleHTML ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                        Exportiere...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faDownload} className="mr-2" />
                        {selectedArticles.length <= 1 ? 'HTML Exportieren' : 'ZIP Exportieren'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// **KOMPAKTES ATTACHMENT COMPONENT**
const AttachmentItem: React.FC<{ 
  attachment: any; 
  submissionId: string; 
}> = ({ attachment, submissionId }) => {
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const fileName = typeof attachment === 'object' && attachment.originalName 
    ? attachment.originalName 
    : (typeof attachment === 'string' ? attachment.split(/[/\\]/).pop() : attachment);
  
  const mimeType = typeof attachment === 'object' ? attachment.mimeType : undefined;
  
  const isImageFile = (fileName: string, mimeType?: string): boolean => {
    if (mimeType) {
      return mimeType.startsWith('image/');
    }
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    return imageExtensions.includes(extension);
  };

  const isImage = isImageFile(fileName, mimeType);

  const handleDownload = async () => {
    try {
      setDownloading(true);
      await downloadSubmissionAttachment(submissionId, fileName);
    } catch (error: any) {
      console.error(`❌ Download error:`, error);
      alert(`Fehler beim Download: ${error.message}`);
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    if (isImage && showPreview && !imagePreviewUrl) {
      getAttachmentPreview(submissionId, fileName).then((url: string | null) => {
        if (url) {
          setImagePreviewUrl(url);
        }
      });
    }
    
    return () => {
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [isImage, showPreview, submissionId, fileName, imagePreviewUrl]);

  return (
    <div className="bg-gray-50 rounded p-2 border border-gray-200">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center space-x-1.5 flex-1 min-w-0">
          <FontAwesomeIcon icon={faFileAlt} className="text-gray-600 flex-shrink-0 text-xs" />
          <span className="text-xs font-medium text-gray-700 truncate" title={fileName}>
            {fileName}
          </span>
        </div>
        
        <div className="flex items-center space-x-1 flex-shrink-0">
          {isImage && (
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="flex items-center px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs hover:bg-green-200 transition-colors"
              title="Vorschau anzeigen/verbergen"
            >
              <FontAwesomeIcon icon={showPreview ? faEyeSlash : faEye} className="mr-1" />
              {showPreview ? 'Verbergen' : 'Vorschau'}
            </button>
          )}
          
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="flex items-center px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200 disabled:opacity-50 transition-colors"
            title={`Download: ${fileName}`}
          >
            {downloading ? (
              <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-1" />
            ) : (
              <FontAwesomeIcon icon={faDownload} className="mr-1" />
            )}
            {downloading ? 'Lädt...' : 'Download'}
          </button>
        </div>
      </div>
      
      {isImage && showPreview && imagePreviewUrl && (
        <div className="mt-2 pt-2 border-t border-gray-300">
          <img 
            src={imagePreviewUrl} 
            alt={`Vorschau: ${fileName}`}
            className="max-w-full max-h-32 border border-gray-300 rounded shadow-sm bg-white object-contain"
            style={{ imageRendering: 'crisp-edges' }}
          />
        </div>
      )}
    </div>
  );
};

export default SubmissionAnswersModal;